import React, { useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"

import { Box } from "@infotech/uikit-grid"
import { selectAllStatus } from "src/engine/modules/status/selectors"
import { selectEventCustomField } from "src/engine/modules/features/selectors"
import Select from "@infotech/uikit-select"
import Input from "@infotech/uikit-input"
import FieldSet from "@infotech/uikit-fieldset"
import Modal from "src/components/modals/Modal"
import get from "lodash/get"
import uniq from "lodash/uniq"
import GlobalEscapeHandle from "src/components/GlobalEscapeHandle"
import { typeCustomOptions } from "src/constants/EventTypeOptions/typeCustomOptions"

const FilterModal = ({
  closeModal,
  resourceTypes,
  planResourceTypes,
  resourceTypeIdFilter,
  eventTypeIdFilter,
  statusCodeFilter,
  eventCustomFieldFilter,
  filterCalendarBy,
  eventTypes,
  statuses: { data: statusOptions },
  customFieldFeature,
  isStatusAvailable,
}) => {
  const {
    setResourceTypeFilter,
    setEventStatusFilter,
    setEventTypeFilter,
    setEventCustomFieldFilter,
  } = filterCalendarBy

  // custom field logic
  const withCustomField = get(customFieldFeature, `enable`)
  const customFieldName = get(customFieldFeature, `parameters.name`)
  const customFieldPlaceholder = get(
    customFieldFeature,
    `parameters.filterPlaceholder`,
  )

  const data =
    get(planResourceTypes, `data`) || get(resourceTypes, `data`) || []
  const resourceTypesOptions = data.map(item => ({
    label: item.name,
    value: item.id,
  }))
  // Initials
  const initialResourceFilter =
    resourceTypeIdFilter.map(item => item.value || item) || []
  const initialEventFilter =
    eventTypeIdFilter.filter(item => !!item).map(item => item.value || item) ||
    []
  const initialStatusFilter =
    statusCodeFilter.filter(item => !!item).map(item => item.value || item) ||
    []
  const initialCustomFieldFilter = eventCustomFieldFilter || []
  const initialValues = initialResourceFilter.concat(
    initialStatusFilter,
    initialEventFilter,
    initialCustomFieldFilter,
  )
  // values state
  const [resourceTypeValue, onChangeResourceTypeValue] = useState(
    resourceTypeIdFilter,
  )
  const [eventTypeValue, onChangeEventTypeValue] = useState(eventTypeIdFilter)
  const [eventStatusValue, onChangeEventStatusValue] = useState(
    statusCodeFilter,
  )
  const [eventCustomFieldValue, onChangeEventCustomFieldValue] = useState(
    eventCustomFieldFilter,
  )
  const allValues = resourceTypeValue
    .concat(eventTypeValue, eventStatusValue, eventCustomFieldValue || [])
    .map(item => item.value || item)
  const uniqArray = uniq(initialValues.concat(allValues))

  const disableApply =
    uniqArray.length === allValues.length &&
    allValues.length === initialValues.length
  const disableReset = Boolean(!allValues.length)

  const resetFilter = () => {
    onChangeEventStatusValue([])
    onChangeEventTypeValue([])
    onChangeResourceTypeValue([])
    onChangeEventCustomFieldValue(``)
  }
  const applyFilter = () => {
    setResourceTypeFilter(resourceTypeValue)
    setEventStatusFilter(eventStatusValue)
    setEventTypeFilter(eventTypeValue)
    setEventCustomFieldFilter(eventCustomFieldValue)
    closeModal()
  }
  return (
    <>
      <Modal
        title="Фильтр"
        isOpen
        width={440}
        buttonConf={[
          {
            text: `Применить`,
            onClick: applyFilter,
            isOk: true,
            disabled: disableApply,
          },
          {
            text: `Закрыть`,
            onClick: closeModal,
            disabled: false,
          },
          {
            text: `Сбросить`,
            onClick: resetFilter,
            disabled: disableReset,
          },
        ]}
        fullHeight
        onCancel={closeModal}
      >
        <Box p="2md">
          <FieldSet caption="Тип ресурса">
            <Select
              palceholder="Выберите тип ресурса"
              value={resourceTypeValue}
              isClearable
              isMulti
              options={resourceTypesOptions}
              onChange={({ target: { value: option } }) => {
                onChangeResourceTypeValue(option || [])
              }}
            />
          </FieldSet>
        </Box>
        <Box p="2md">
          <FieldSet caption="Тип события">
            <Select
              palceholder="Выберите тип события"
              value={eventTypeValue}
              isClearable
              isMulti
              options={eventTypes.map(item => ({
                value: item.id,
                label: item.name,
                color: item.color,
                data: item,
              }))}
              onChange={({ target: { value: option } }) => {
                onChangeEventTypeValue(option || [])
              }}
              components={typeCustomOptions(eventTypes)}
            />
          </FieldSet>
        </Box>
        {isStatusAvailable && (
          <Box p="2md">
            <FieldSet caption="Статус">
              <Select
                palceholder="Выберите статус"
                value={eventStatusValue}
                isClearable
                isMulti
                options={statusOptions.map(item => ({
                  value: item.code,
                  label: item.name,
                }))}
                onChange={({ target: { value: option } }) => {
                  onChangeEventStatusValue(option || [])
                }}
              />
            </FieldSet>
          </Box>
        )}
        {withCustomField && (
          <Box p="2md">
            <FieldSet caption={customFieldName} />
            <Input
              placeholder={customFieldPlaceholder}
              value={eventCustomFieldValue}
              onValueChange={value =>
                onChangeEventCustomFieldValue(value || ``)
              }
            />
          </Box>
        )}
      </Modal>
      <GlobalEscapeHandle onEscapePress={closeModal} />
    </>
  )
}

FilterModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  resourceTypes: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          id: PropTypes.number,
        }),
      ),
    }),
  ),
  planResourceTypes: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.number,
      }),
    ),
  }),
  resourceTypeIdFilter: PropTypes.arrayOf(PropTypes.number).isRequired,
  eventTypeIdFilter: PropTypes.arrayOf(PropTypes.number).isRequired,
  statusCodeFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  filterCalendarBy: PropTypes.shape({
    setResourceTypeFilter: PropTypes.func.isRequired,
    setEventStatusFilter: PropTypes.func.isRequired,
    setEventTypeFilter: PropTypes.func.isRequired,
  }).isRequired,
  eventTypes: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
}

FilterModal.defaultProps = {
  resourceTypes: {},
  planResourceTypes: {},
}

export default connect(
  state => ({
    statuses: selectAllStatus(state),
    customFieldFeature: selectEventCustomField(state),
  }),
  null,
)(FilterModal)
